import React, { useContext, useState, useEffect } from "react";
import { get, put, post } from "../../api";
import { HOST } from "../../constants/api";
import { AuthContext } from "../../contexts/Auth";
import CustomerContext from "../../contexts/Customer";
import { SYS_ADMIN, TENANT_ADMIN, CUSTOMER_USER } from "../../constants/roles";
import { isAuthorized, hasPermission } from "../../helpers/authorization";
import CustomerInfoForm from "../../components/CustomerInfoForm";
import FrameHeader from "../../components/FrameHeader";
import Frame from "../../components/Frame";
import Button from "../../components/Button";
import styles from "./CustomerInfo.module.scss";

export default function CustomerInfo() {
  const { customer, setCustomer } = useContext(CustomerContext);
  const { state } = useContext(AuthContext);
  const [saving, setSaving] = useState(false);
  const [generatingKey, setGeneratingKey] = useState(false);
  const [apiKey, setApiKey] = useState(null); // full key value (from server or just generated)
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);

  const userRoles = state.user.roles || [];
  const userPerms = state.user.perms || [];

  const isTenantAdmin =
    isAuthorized(userRoles, [TENANT_ADMIN]) &&
    hasPermission(userPerms, "ADMIN");
  const isSysOrTenantAdmin =
    isAuthorized(userRoles, [SYS_ADMIN, TENANT_ADMIN]) &&
    hasPermission(userPerms, "ADMIN");
  const canManageApiKey = isSysOrTenantAdmin;

  // Load existing key from the ApiKey table on mount.
  useEffect(() => {
    if (!canManageApiKey || !customer?.id) return;
    const url = `/customers/${customer.id}/apikey`;
    get(url)
      .then((res) => {
        if (res?.api_key) setApiKey(res.api_key);
      })
      .catch(() => {});
  }, [customer?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateCustomer = async (customer) => {
    setSaving(true);
    const res = await put(`/customers/${customer.id}`, { ...customer });
    setCustomer(res);
    setSaving(false);
  };

  const generateApiKey = async () => {
    setGeneratingKey(true);
    setApiKey(null);
    setRevealed(true); // show the key immediately after generation
    setCopied(false);
    const url = `/customers/${customer.id}/apikey`;
    const res = await post(url);
    if (res?.api_key) setApiKey(res.api_key);
    setGeneratingKey(false);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(apiKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const maskedKey = apiKey
    ? "•".repeat(Math.max(0, apiKey.length - 4)) + apiKey.slice(-4)
    : null;

  return (
    <>
      <FrameHeader title="Info" />
      <CustomerInfoForm
        state={customer}
        saving={saving}
        onSave={(customer) => updateCustomer(customer)}
      />

      {canManageApiKey && (
        <div className={styles.apiSectionGap}>
          <FrameHeader title="API Access" />
          <Frame>
            <div className={styles.apiSection}>
              <p className={styles.apiDescription}>
                Generate an API key to access this customer's assets, devices,
                and activity programmatically via the <code>/api/*</code>{" "}
                endpoints.{" "}
                <a
                  href={`${HOST}/swagger-ui/`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.apiDocsLink}
                >
                  View API Documentation ↗
                </a>
              </p>

              {apiKey && (
                <div className={styles.apiKeyBox}>
                  <code className={styles.apiKeyValue}>
                    {revealed ? apiKey : maskedKey}
                  </code>
                  <Button
                    theme="secondary"
                    onClick={() => setRevealed((r) => !r)}
                    fixedWidth={70}
                  >
                    {revealed ? "Hide" : "Show"}
                  </Button>
                  {revealed && (
                    <Button
                      theme="secondary"
                      onClick={copyToClipboard}
                      fixedWidth={90}
                    >
                      {copied ? "Copied!" : "Copy"}
                    </Button>
                  )}
                </div>
              )}

              <div>
                <Button
                  theme="primary"
                  onClick={generateApiKey}
                  isLoading={generatingKey}
                  disabled={generatingKey}
                >
                  {apiKey ? "Regenerate API Key" : "Generate API Key"}
                </Button>
              </div>
            </div>
          </Frame>
        </div>
      )}
    </>
  );
}
